import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface CompensationTerms {
  compensationModel: 'hourly' | 'salary' | null
  hourlyRate: number | null
  salaryAmount: number | null
  compensationEffectiveDate: string | null
  paymentFrequency: 'weekly' | 'biweekly' | 'monthly' | null
}

export interface TimeEntry {
  id: string
  workDate: string
  hours: number
  note: string | null
  createdAt: string
  lessonId: string | null
}

export interface EarningsEntry {
  id: string
  periodStart: string
  periodEnd: string
  hoursWorked: number | null
  grossAmount: number
  paymentFrequency: 'weekly' | 'biweekly' | 'monthly' | null
  note: string | null
  createdAt: string
  lessonId: string | null
}

interface RpcResult {
  ok: boolean
  code?: string
}

interface SettleResult {
  ok: boolean
  code?: string
  hours?: number
  gross_amount?: number
}

/**
 * Compensation terms + the hours-worked/earnings-history ledger for one employee
 * (migration 086) — a manual record-keeping log, not an automated payroll run. Every
 * write below goes through its own SECURITY DEFINER RPC (set_employee_compensation /
 * log_employee_time_entry / log_employee_earnings_entry); this hook never writes
 * school_memberships or the ledger tables directly, matching the project-wide convention
 * that this table has no client-facing UPDATE/INSERT RLS policy at all.
 */
export function useEmployeeLedger(schoolMembershipId: string | null) {
  const [compensation, setCompensation] = useState<CompensationTerms | null>(null)
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([])
  const [earningsEntries, setEarningsEntries] = useState<EarningsEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingCompensation, setSavingCompensation] = useState(false)
  const [loggingTime, setLoggingTime] = useState(false)
  const [loggingEarnings, setLoggingEarnings] = useState(false)

  const fetchAll = useCallback(async () => {
    if (!schoolMembershipId) {
      setCompensation(null); setTimeEntries([]); setEarningsEntries([]); setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [{ data: row, error: rowErr }, { data: times }, { data: earnings }] = await Promise.all([
        supabase
          .from('school_memberships')
          .select('compensation_model, hourly_rate, salary_amount, compensation_effective_date, payment_frequency')
          .eq('id', schoolMembershipId)
          .maybeSingle(),
        supabase
          .from('employee_time_entries')
          .select('id, work_date, hours, note, created_at, lesson_id')
          .eq('school_membership_id', schoolMembershipId)
          .order('work_date', { ascending: false }),
        supabase
          .from('employee_earnings_entries')
          .select('id, period_start, period_end, hours_worked, gross_amount, payment_frequency, note, created_at, lesson_id')
          .eq('school_membership_id', schoolMembershipId)
          .order('period_start', { ascending: false }),
      ])

      if (rowErr) { setError('Could not load compensation terms.'); return }

      setCompensation(row ? {
        compensationModel: row.compensation_model,
        hourlyRate: row.hourly_rate === null || row.hourly_rate === undefined ? null : Number(row.hourly_rate),
        salaryAmount: row.salary_amount === null || row.salary_amount === undefined ? null : Number(row.salary_amount),
        compensationEffectiveDate: row.compensation_effective_date,
        paymentFrequency: row.payment_frequency,
      } : null)

      setTimeEntries((times ?? []).map(t => ({
        id: t.id, workDate: t.work_date, hours: Number(t.hours), note: t.note, createdAt: t.created_at,
        lessonId: t.lesson_id ?? null,
      })))
      setEarningsEntries((earnings ?? []).map(e => ({
        id: e.id,
        periodStart: e.period_start,
        periodEnd: e.period_end,
        hoursWorked: e.hours_worked === null || e.hours_worked === undefined ? null : Number(e.hours_worked),
        grossAmount: Number(e.gross_amount),
        paymentFrequency: e.payment_frequency,
        note: e.note,
        createdAt: e.created_at,
        lessonId: e.lesson_id ?? null,
      })))
    } finally {
      setLoading(false)
    }
  }, [schoolMembershipId])

  useEffect(() => { fetchAll() }, [fetchAll])

  const saveCompensation = useCallback(async (fields: {
    compensationModel: 'hourly' | 'salary'
    hourlyRate: number | null
    salaryAmount: number | null
    effectiveDate: string
    paymentFrequency: 'weekly' | 'biweekly' | 'monthly'
  }): Promise<RpcResult> => {
    if (!schoolMembershipId || savingCompensation) return { ok: false, code: 'busy' }
    setSavingCompensation(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('set_employee_compensation', {
        p_school_membership_id: schoolMembershipId,
        p_compensation_model: fields.compensationModel,
        p_hourly_rate: fields.hourlyRate ?? undefined,
        p_salary_amount: fields.salaryAmount ?? undefined,
        p_effective_date: fields.effectiveDate,
        p_payment_frequency: fields.paymentFrequency,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as unknown as RpcResult
      if (result?.ok) await fetchAll()
      return result ?? { ok: false, code: 'internal_error' }
    } finally {
      setSavingCompensation(false)
    }
  }, [schoolMembershipId, savingCompensation, fetchAll])

  const logTimeEntry = useCallback(async (fields: {
    workDate: string; hours: number; note?: string
  }): Promise<RpcResult> => {
    if (!schoolMembershipId || loggingTime) return { ok: false, code: 'busy' }
    setLoggingTime(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('log_employee_time_entry', {
        p_school_membership_id: schoolMembershipId,
        p_work_date: fields.workDate,
        p_hours: fields.hours,
        p_note: fields.note || undefined,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as unknown as RpcResult
      if (result?.ok) await fetchAll()
      return result ?? { ok: false, code: 'internal_error' }
    } finally {
      setLoggingTime(false)
    }
  }, [schoolMembershipId, loggingTime, fetchAll])

  const logEarningsEntry = useCallback(async (fields: {
    periodStart: string; periodEnd: string; grossAmount: number
    hoursWorked?: number; paymentFrequency?: 'weekly' | 'biweekly' | 'monthly'; note?: string
  }): Promise<RpcResult> => {
    if (!schoolMembershipId || loggingEarnings) return { ok: false, code: 'busy' }
    setLoggingEarnings(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('log_employee_earnings_entry', {
        p_school_membership_id: schoolMembershipId,
        p_period_start: fields.periodStart,
        p_period_end: fields.periodEnd,
        p_gross_amount: fields.grossAmount,
        p_hours_worked: fields.hoursWorked ?? undefined,
        p_payment_frequency: fields.paymentFrequency ?? undefined,
        p_note: fields.note || undefined,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as unknown as RpcResult
      if (result?.ok) await fetchAll()
      return result ?? { ok: false, code: 'internal_error' }
    } finally {
      setLoggingEarnings(false)
    }
  }, [schoolMembershipId, loggingEarnings, fetchAll])

  const [settling, setSettling] = useState(false)

  const settlePeriod = useCallback(async (fields: {
    periodStart: string; periodEnd: string
  }): Promise<SettleResult> => {
    if (!schoolMembershipId || settling) return { ok: false, code: 'busy' }
    setSettling(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('settle_employee_earnings_period', {
        p_school_membership_id: schoolMembershipId,
        p_period_start: fields.periodStart,
        p_period_end: fields.periodEnd,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as unknown as SettleResult
      if (result?.ok) await fetchAll()
      return result ?? { ok: false, code: 'internal_error' }
    } finally {
      setSettling(false)
    }
  }, [schoolMembershipId, settling, fetchAll])

  return {
    compensation, timeEntries, earningsEntries, loading, error,
    savingCompensation, loggingTime, loggingEarnings, settling,
    saveCompensation, logTimeEntry, logEarningsEntry, settlePeriod, refresh: fetchAll,
  }
}
